import { useQuery } from "@tanstack/react-query";

import { get } from "@/api";
import { Models } from "@/api/types";
import { useFeatureFlag } from "@/services/Flags";

type Result = {
  showReferralRewards: boolean;
  showLegacyReferral: boolean;
  referralRewardForPlatformLocation?: Models<"DisplayAvailableReferralReward"> | null;
  activeSignupReward?: Models<"DisplayActiveReward"> | null;
};
export function useReferralRewardsFeature(): Result {
  const referralFeatureFlagEnabled = useFeatureFlag("referral-rewards-phase-2");

  const { data: platform } = useQuery({
    queryKey: ["/platform/current"],
    queryFn: () => get("/platform/current"),
  });

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
  });

  const { data: referralRewardForPlatformLocation } = useQuery({
    queryKey: [
      "/incentives/{platformLocationId}/available-referral-reward",
      platform?.id,
    ],
    queryFn: () =>
      get("/incentives/{platformLocationId}/available-referral-reward", {
        platformLocationId: user?.platformLocation?.id ?? "",
      }),
    onError: () => null,
    enabled: referralFeatureFlagEnabled,
  });

  const { data: activeSignupReward } = useQuery({
    queryKey: ["/incentives/{platformLocationId}/active-signup-reward"],
    queryFn: () =>
      get("/incentives/{platformLocationId}/active-signup-reward", {
        platformLocationId: user?.platformLocation?.id ?? "",
      }),
    onError: () => null,
  });

  const { data: referralHistoryForPerson } = useQuery({
    queryKey: [
      "/{platformId}/referral-history/{personId}",
      platform?.id,
      user?.id,
    ],
    queryFn: () =>
      get("/incentives/{platformId}/referral-history/{personId}", {
        platformId: platform?.id || "",
        personId: user?.id || "",
      }),
    enabled: !!platform?.id && !!user?.id,
    onError: () => null,
  });

  const shouldShowReferralRewards = () => {
    if (
      referralFeatureFlagEnabled &&
      !!referralHistoryForPerson?.referralHistory?.length
    ) {
      return true;
    }

    return (
      referralFeatureFlagEnabled &&
      !!referralRewardForPlatformLocation &&
      !!activeSignupReward &&
      !user?.isGuest
    );
  };

  return {
    referralRewardForPlatformLocation,
    activeSignupReward,
    showReferralRewards: shouldShowReferralRewards(),
    showLegacyReferral:
      !referralFeatureFlagEnabled ||
      (referralFeatureFlagEnabled &&
        !user?.isGuest &&
        !referralRewardForPlatformLocation),
  };
}
